<template>
  <div class="d-flex flex-column gap-6 pa-6 pb-16" data-testid="my-reporting-units-view">
      <PageHeader 
        :title="$nova.labelsName('my_reporting_units_page', 'threshold_label', 'My Reporting Units')"
        :loading="loading"
        @refresh="getRefreshData" 
        data-testid="my-reporting-units__header"
      />
      <div>
        <cfilter
          cycletype="Actual" 
          :showrutype="true"
          :showrole="false" 
          :showyear="true" 
          :showperiod="true" 
          :shownetwork="true" 
          :showregion="true" 
          :showcountry="true" 
          :showreportingunit="true"
          :showSync="false"
          :showFavourite="false"
          :showReset="true"
          :showApply="true"
          @newdata="handleFilterData"
          data-testid="my-reporting-units__filter" 
        />
      </div>
      <v-card class="pa-3 reportingcard d-flex flex-column gap-3"  data-testid="my-reporting-units__card" rounded-xl> 
        <div class="d-flex" v-if="formattedData.length !== 0">
          <ReportingUnitCount :count="meta.totalCount" :loading="loading" data-testid="my-reporting-units__count" />
        </div>
        <div  class="d-flex flex-column gap-6">
          <ReportingUnitTable
            :headers="headers"
            :items="formattedData"
            :loading="loading"
            :page.sync="page"
            :meta="meta"
            :perpage="perpage"
            :filter-options="filterOptions"
            :materiality-loading="materialityLoading"
            :selected-user-id="selectedUserId"
            @loadNextPage="loadNextPage"
            @loadFilterData="getDatadata"
            @view-details="openRUDetail"
            data-testid="my-reporting-units__table"
          />
        </div>
      </v-card>
      <ReportingUnitDialog
        v-if="rudetaildialog"
        v-model="rudetaildialog"
        :loading="rudetailloading"
        :detail="rudetail"
        :user-id="userid"
        data-testid="my-reporting-units__dialog"
      />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { getMyReportingUnitsTableHeaders, getMyReportingUnitsViewOnlyHeaders } from '../../utils/constants';
import { formatReportingUnitsData } from '../../utils/myReportingUnitsUtils';
import PageHeader from '../../components/PageHeader.vue';
import ReportingUnitCount from './components/ReportingUnitCount.vue';
import ReportingUnitDialog from './components/ReportingUnitDialog.vue';
import ReportingUnitTable from './components/ReportingUnitTable.vue';
import cfilter from '../Filter.vue';

export default {
  name: 'ReportingUnits',
  components: {
    PageHeader,
    ReportingUnitCount,
    ReportingUnitDialog,
    ReportingUnitTable,
    cfilter,
  },
  data: function () {
    return {
      // Page identification
      pageName: "my_reporting_units_page",
      right: "commentary_reportingunit",
      
      // Loading states
      loading: false,
      filterLoading: false,
      materialityLoading: false,
      rudetailloading: false,
      
      // Table data
      formattedData: [],
      fullRUList: {},
      page: 1,
      perpage: 25,
      
      // Filter data
      filterOptions: [],
      previousFilter: {},
      selectedFilter: {},
      filterSelected: false,
      selectedyear: '',
      selectedperiod: '',
      filtersReady: false,
      pendingInitialLoad: true,
      
      // Helper maps for data formatting
      rucodenamemap: {},
      rucodeidmap: {},
      
      // RU Detail dialog
      rudetaildialog: false,
      userid: '',
      
      // Table headers - loaded from constants
      headers: getMyReportingUnitsTableHeaders(this.$nova.labelsName),
    }
  },
  props: {
    isViewOnly: {
      default: false,
      required: false,
      type: Boolean
    },
    selectedUserId: {
      default: undefined,
      required: false,
      type: String
    }
  },
  computed: {
    ...mapGetters('varianceanalysis/reportingUnit', [
      'getRUFilters',
      'getMyReportingUnits',
      'getMyReportingUnitsMeta',
      'getMaterialityData',
      'getRUDetail'
    ]),
    meta() {
        return this.getMyReportingUnitsMeta || { totalCount: 0 };
    },
    rudetail() {
        return this.getRUDetail || {};
    }
  },
  activated() {
  },
  mounted() {
    console.log('[MyReportingUnits] mounted: Component initialized');
    this.userid = this.$store.state.user.id;
  },
  created() {
     if (this.isViewOnly) {
        this.headers = getMyReportingUnitsViewOnlyHeaders(this.$nova.labelsName);
     }
  },
  beforeDestroy() {
    this.RESET_STATE();
  },
  methods: {
    ...mapMutations('varianceanalysis/reportingUnit', ['RESET_STATE']),
    ...mapActions('varianceanalysis/reportingUnit', [
      'fetchRUFilters',
      'fetchMyReportingUnits',
      'fetchMateriality',
      'fetchRUDetail'
    ]),

    refreshData() {
      this.userid = this.$store.state.user.id;
      this.formattedData = []
      this.getData(1);
    },

    calculateTableFilters() {
      const types = new Set();
      this.formattedData.forEach(item => {
        if (item.type) types.add(item.type);
      });
      
      this.filterOptions = [
        {
          key: 'type',
          items: Array.from(types).map(t => ({ 
             text: t === 'ru' ? 'RU' : t === 'jv' ? 'JV' : t.toUpperCase(), 
             value: t 
          })).sort((a,b) => a.text.localeCompare(b.text))
        }
      ];
    },
    
    getRefreshData() {
      this.userid = this.$store.state.user.id;
      this.formattedData = [];
      // Force refresh when user explicitly clicks refresh
      this.getDataWithForceRefresh(1);
    },
    
    async getDataWithForceRefresh(pageInput = this.page) {
      this.loading = true;
      this.previousFilter = {};
      
      if (this.selectedFilter && Object.keys(this.selectedFilter).length > 0) {
        Object.keys(this.selectedFilter).forEach(key => {
            const val = this.selectedFilter[key];
            if (val && val !== 'all') {
               if (key === 'selectedyear') this.previousFilter.year = val;
               else if (key === 'selectedperiod') this.previousFilter.period = val;
               else if (key === 'selectednetwork') this.previousFilter.network = [val];
               else if (key === 'selectedregion') this.previousFilter.region = [val];
               else if (key === 'selectedcountry') this.previousFilter.country = [val];
               else if (key === 'selectedreportingunit') {
                 this.previousFilter.reportingunit = [val];
                 if (this.fullRUList && this.fullRUList[val]) {
                    this.previousFilter.code = this.fullRUList[val].code;
                 }
               }
               else if (key === 'selectedReportingUnitType') this.previousFilter.ruTypes = [val];
            }
        });
      }
      
      // Resolve page index explicitly
      let targetPage = this.page;
      if (pageInput) {
          if (typeof pageInput === 'object' && pageInput.page) targetPage = pageInput.page;
          else if (typeof pageInput === 'number') targetPage = pageInput;
      }
      const pageIndex = Math.max(0, targetPage - 1);

      this.previousFilter.page = pageIndex;
      this.previousFilter.limit = this.perpage;
      
      if (this.selectedUserId !== undefined) this.previousFilter.user_id = this.selectedUserId;

      this.previousFilter.cycletype = 'Actual';

      try {
        const client = this.$store.state.client;
        console.log('[MyReportingUnits] getDataWithForceRefresh: calling fetchMyReportingUnits', { filter: this.previousFilter, client, forceRefresh: true });
        await this.fetchMyReportingUnits({ 
          filter: this.previousFilter,
          client: client, 
          axiosInstance: this.axios,
          forceRefresh: true 
        });
        console.log('[MyReportingUnits] getDataWithForceRefresh: API call completed');
        const { formattedArray, ruCodeNameMap, ruCodeIdMap } = formatReportingUnitsData(this.getMyReportingUnits, pageIndex, this.perpage);
        this.formattedData = formattedArray;
        this.calculateTableFilters();
        this.rucodenamemap = ruCodeNameMap;
        this.rucodeidmap = ruCodeIdMap;
        
        this.prepareMaterialityLoader();
        this.fetchMaterialityData();
      } catch (fetchError) {
         // Error handled in store
      } finally {
          this.loading = false;
      }
    },

    loadNextPage(val) {
      this.getData(val);
    },

    async getData(pageInput = this.page) {
      this.loading = true;
      this.previousFilter = {};
      
      // Merge selected filters
      if (this.selectedFilter && Object.keys(this.selectedFilter).length > 0) {
        Object.keys(this.selectedFilter).forEach(key => {
            const val = this.selectedFilter[key];
             // Filter.vue sends 'all' for no selection, API expects exclusion or specific value
            if (val && val !== 'all') {
               // API expects arrays for most fields
               if (key === 'selectedyear') this.previousFilter.year = val;
               else if (key === 'selectedperiod') this.previousFilter.period = val;
               else if (key === 'selectednetwork') this.previousFilter.network = [val];
               else if (key === 'selectedregion') this.previousFilter.region = [val];
               else if (key === 'selectedcountry') this.previousFilter.country = [val];
               else if (key === 'selectedreportingunit') {
                 this.previousFilter.reportingunit = [val];
                 if (this.fullRUList && this.fullRUList[val]) {
                    this.previousFilter.code = this.fullRUList[val].code;
                 }
               }
               else if (key === 'selectedReportingUnitType') this.previousFilter.ruTypes = [val];
            }
        });
      }
      
      // Resolve page index explicitly
      let targetPage = this.page;
      if (pageInput) {
          if (typeof pageInput === 'object' && pageInput.page) targetPage = pageInput.page;
          else if (typeof pageInput === 'number') targetPage = pageInput;
      }
      const pageIndex = Math.max(0, targetPage - 1);

      this.previousFilter.page = pageIndex;
      this.previousFilter.limit = this.perpage;
      
      if (this.selectedUserId !== undefined) this.previousFilter.user_id = this.selectedUserId;
      this.previousFilter.cycletype = 'Actual';

      try {
        const client = this.$store.state.client;
        console.log('[MyReportingUnits] getData: calling fetchMyReportingUnits', { filter: this.previousFilter, client, forceRefresh: false });
        const response = await this.fetchMyReportingUnits({ 
          filter: this.previousFilter,
          client: client, 
          axiosInstance: this.axios,
          forceRefresh: false 
        });
        console.log('[MyReportingUnits] getData: response', { fromCache: response?.fromCache });
        
        // Always format and display data, whether from cache or API
        const { formattedArray, ruCodeNameMap, ruCodeIdMap } = formatReportingUnitsData(this.getMyReportingUnits, pageIndex, this.perpage);
        this.formattedData = formattedArray;
        this.calculateTableFilters();
        this.rucodenamemap = ruCodeNameMap;
        this.rucodeidmap = ruCodeIdMap;
        
        this.prepareMaterialityLoader();
        this.fetchMaterialityData();
      } catch (fetchError) {
         // Error handled in store
      } finally {
          this.loading = false;
      }
    },

    /**
     * Handles filter data update from Filter component
     */
    handleFilterData(d) {
      if (d.key === 'loading') {
        this.filterLoading = d.value;
        return;
      }

      if (d.key === 'reportingunitlist') {
        this.fullRUList = d.value;
      }
      
      // Map keys to selectedFilter
      this.$set(this.selectedFilter, d.key, d.value);
      
      // Track when essential filters are available
      if (['selectedyear', 'selectedperiod', 'selectednetwork', 'selectedregion', 'selectedcountry', 'selectedreportingunit'].includes(d.key)) {
        // Check if we have all essential filters
        const hasEssentialFilters = this.selectedFilter.selectedyear && 
                                     this.selectedFilter.selectedperiod &&
                                     this.selectedFilter.selectednetwork !== undefined &&
                                     this.selectedFilter.selectedregion !== undefined &&
                                     this.selectedFilter.selectedcountry !== undefined &&
                                     this.selectedFilter.selectedreportingunit !== undefined;
        
        if (hasEssentialFilters && !this.filtersReady) {
          console.log('[MyReportingUnits] handleFilterData: All essential filters ready');
          this.filtersReady = true;
          
          // Trigger initial load if this is the first time filters are ready
          if (this.pendingInitialLoad) {
            console.log('[MyReportingUnits] handleFilterData: Triggering initial data load');
            this.pendingInitialLoad = false;
            this.page = 1;
            this.getData(1);
            return;
          }
        }
      }

      if (d.reload) {
        console.log('[MyReportingUnits] handleFilterData: Reload requested');
        this.page = 1;
        this.getData(1);
      }
    },
    /**
     * Handles filter application from ReportingUnitsFilter component
     * Resets page to 1 and stores filter for pagination persistence
     * @param {Object} filterWrapper - Filter data from filter component
     */
    async getDatadata(filterWrapper) {
      this.page = 1;
      this.loading = true;
      this.previousFilter = {};

      // 1. Populate Global Filters from this.selectedFilter
      if (this.selectedFilter && Object.keys(this.selectedFilter).length > 0) {
        Object.keys(this.selectedFilter).forEach(key => {
            const val = this.selectedFilter[key];
            if (val && val !== 'all') {
               if (key === 'selectedyear') this.previousFilter.year = val;
               else if (key === 'selectedperiod') this.previousFilter.period = val;
               else if (key === 'selectednetwork') this.previousFilter.network = [val];
               else if (key === 'selectedregion') this.previousFilter.region = [val];
               else if (key === 'selectedcountry') this.previousFilter.country = [val];
               else if (key === 'selectedreportingunit') {
                 this.previousFilter.reportingunit = [val];
                 if (this.fullRUList && this.fullRUList[val]) {
                    this.previousFilter.code = this.fullRUList[val].code;
                 }
               }
               else if (key === 'selectedReportingUnitType') this.previousFilter.ruTypes = [val];
            }
        });
      }

      // 2. Merge Table Column Filters
      const rawFilterObject = filterWrapper?.filter;
      if (rawFilterObject && typeof rawFilterObject === "object") {  
        Object.keys(rawFilterObject).forEach(filterKey => {
           const values = rawFilterObject[filterKey];
           if (values && values.length > 0) {
               this.previousFilter[filterKey] = values;
           }
        });
      }

      // Page is always 1 (0-based) for filtering
      const pageIndex = 0;

      this.previousFilter.page = pageIndex;
      this.previousFilter.limit = this.perpage;
      if (this.selectedUserId !== undefined) this.previousFilter.user_id = this.selectedUserId;

      // Convert to 0-based page index for API
      this.previousFilter.cycletype = 'Actual';
      
      try {
        const client = this.$store.state.client;
        console.log('[MyReportingUnits] getDatadata: calling fetchMyReportingUnits', { filter: this.previousFilter, client, forceRefresh: false });
        const response = await this.fetchMyReportingUnits({ 
          filter: this.previousFilter,
          client: client, 
          axiosInstance: this.axios,
          forceRefresh: false 
        });
        console.log('[MyReportingUnits] getDatadata: response', { fromCache: response?.fromCache });
        
        // Always format and display data, whether from cache or API
        const { formattedArray, ruCodeNameMap, ruCodeIdMap } = formatReportingUnitsData(this.getMyReportingUnits, pageIndex, this.perpage);
        this.formattedData = formattedArray;
        this.calculateTableFilters();
        this.rucodenamemap = ruCodeNameMap;
        this.rucodeidmap = ruCodeIdMap;
        
        this.prepareMaterialityLoader();
        this.fetchMaterialityData();
      } catch (requestError) {
         // Error handled in store
      } finally {
          this.loading = false;
      }
    },




    getFilterResetData(filter) {
      this.previousFilter = filter;
      this.page = 1;
      this.filterSelected = false;
      this.getData(1);
    },
    prepareMaterialityLoader() {
      this.materialityLoading = true;
      this.formattedData.forEach((rowObject) => {
        rowObject.materialityset_actual = null;
        rowObject.materialityset_forecast = null;
        rowObject.materialityflag_actual = null;
        rowObject.materialityflag_forecast = null;
      });
    },
    async fetchMaterialityData() {
      try {
        const filterPayload = { ...(this.previousFilter || {}) };
        await this.fetchMateriality({ filter: filterPayload, axiosInstance: this.axios });
        
        const resultArray = this.getMaterialityData || [];
        const materialityMap = new Map(resultArray.map(unitItem => [unitItem._id, unitItem]));
        
        this.formattedData.forEach((rowObject, rowIndex) => {
            const refreshed = materialityMap.get(rowObject._id);
            if (!refreshed) {
              this.$set(this.formattedData[rowIndex], 'materialityset_actual', false);
              this.$set(this.formattedData[rowIndex], 'materialityset_forecast', false);
              this.$set(this.formattedData[rowIndex], 'materialityflag_actual', "No");
              this.$set(this.formattedData[rowIndex], 'materialityflag_forecast', "No");
              return;
            }
            const updatedValues = {
              materialityset_actual: !!refreshed.materialityset_actual,
              materialityset_forecast: !!refreshed.materialityset_forecast,
              materialityflag_actual: refreshed.materialityset_actual ? "Yes" : "No",
              materialityflag_forecast: refreshed.materialityset_forecast ? "Yes" : "No",
            };
            Object.entries(updatedValues).forEach(([fieldKey, fieldValue]) => {
              if (this.formattedData[rowIndex][fieldKey] !== fieldValue) {
                this.$set(this.formattedData[rowIndex], fieldKey, fieldValue);
              }
            });
          });
      } catch (materialityError) {
        // Error handled in store
        this.formattedData.forEach((rowObject, rowIndex) => {
          if (rowObject.materialityflag_actual === null) {
            this.$set(this.formattedData[rowIndex], 'materialityset_actual', false);
            this.$set(this.formattedData[rowIndex], 'materialityflag_actual', "No");
          }
          if (rowObject.materialityflag_forecast === null) {
            this.$set(this.formattedData[rowIndex], 'materialityset_forecast', false);
            this.$set(this.formattedData[rowIndex], 'materialityflag_forecast', "No");
          }
        });
      } finally {
        this.materialityLoading = false;
      }
    },
    /**
     * Opens the RU detail dialog and fetches detail data
     * @param {string} id - The reporting unit ID
     */
    async openRUDetail(id) {
      this.rudetaildialog = true;
      this.rudetailloading = true;
      try {
        await this.fetchRUDetail({ filter: { reportingunit: id }, axiosInstance: this.axios });
      } catch (err) {
        // Error handled in store
      } finally {
        this.rudetailloading = false;
      }
    },


  },
}
</script>
<style scoped>

</style>